import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { Api } from 'api';
import { formatToSloDate } from 'pages/Sessions/SessionsPage';
import { useState, useEffect } from 'react';

interface Transaction {
  id: string;
  createdAt: string;
  reason: string;
  amount: { formatted: string; value: number };
  balance: { formatted: string };
  // Cart total (what will be invoiced) - may differ from amount if charge was capped
  cartTotal?: { formatted: string; value: number };
  note?: string;
  cartId?: string;
}

interface TransactionSelectionModalProps {
  open: boolean;
  walletId: string;
  ownerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const TransactionSelectionModal = ({
  open,
  walletId,
  ownerName,
  onClose,
  onSuccess,
}: TransactionSelectionModalProps) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && walletId) {
      setLoading(true);
      setError(null);
      setSelectedIds([]);
      Api.billing
        .getUnbilledTransactionsForWallet(walletId)
        .then((response) => {
          const data = response.data?.data || [];
          setTransactions(data);
          // Pre-select all transactions by default
          setSelectedIds(data.map((t: Transaction) => t.id));
        })
        .catch((err) => {
          setError(err?.response?.data?.error?.message || 'Failed to load transactions');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, walletId]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectedIds.length === transactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(transactions.map((t) => t.id));
    }
  };

  const calculateTotal = () => {
    // Use cartTotal (what will be invoiced) when available, fallback to amount
    // Calculate in cents to avoid floating point precision issues
    const totalInCents = transactions
      .filter((t) => selectedIds.includes(t.id))
      .reduce((sum, t) => {
        const value = t.cartTotal?.value ?? Math.abs(t.amount.value);
        return sum + Math.round(value * 100);
      }, 0);
    return (totalInCents / 100).toFixed(2);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await Api.billing.createInvoiceForWallet(walletId, undefined, undefined, selectedIds);
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || 'Failed to create invoice';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Transactions for Invoice - {ownerName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : transactions.length === 0 ? (
          <Typography>No unbilled transactions found for this wallet.</Typography>
        ) : (
          <>
            <TableContainer component={Paper} style={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedIds.length > 0 && selectedIds.length < transactions.length
                        }
                        checked={selectedIds.length === transactions.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow
                      key={transaction.id}
                      hover
                      onClick={() => handleToggle(transaction.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.includes(transaction.id)} />
                      </TableCell>
                      <TableCell>{formatToSloDate(new Date(transaction.createdAt))}</TableCell>
                      <TableCell style={{ textTransform: 'capitalize' }}>
                        {transaction.reason?.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell>{transaction.note || '-'}</TableCell>
                      <TableCell align="right">
                        {transaction.cartTotal?.formatted ?? transaction.amount.formatted}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="space-between" marginTop={2}>
              <Typography>
                Selected: {selectedIds.length} of {transactions.length} transactions
              </Typography>
              <Typography variant="h6">Total: {calculateTotal()} EUR</Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={selectedIds.length === 0 || submitting}
        >
          {submitting ? 'Creating...' : 'Generate Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
